import React, { useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import { ExpandMore, Person, Groups } from "@mui/icons-material";
import AppNavMenuItems from "./AppNavMenuItems";
import TeamNav from "./TeamNav";

interface User {
  id: number;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  isSuperuser?: boolean;
  hasUsablePassword?: string | boolean; // API returns string, but can be boolean in some contexts
}

interface Team {
  id: number;
  name?: string;
  slug?: string;
}

interface AppNavProps {
  user: User | null;
  team: Team | null;
  isTeamAdmin?: boolean;
  activeTab?: string;
  variant?: "dropdown" | "sidebar";
  onNavigate?: () => void; // For closing mobile drawers
}

export default function AppNav({
  user,
  team,
  isTeamAdmin = false,
  activeTab,
  variant = "dropdown",
  onNavigate,
}: AppNavProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (variant === "dropdown") {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuNavigate = () => {
    handleClose();
    onNavigate?.();
  };

  // Get display name for user
  const getUserDisplayName = () => {
    if (!user) return "User";
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username || user.email || "User";
  };

  // Get display name for team/user dropdown button
  const getDropdownDisplayName = () => {
    if (team) {
      return team.name;
    }
    return getUserDisplayName();
  };

  if (variant === "sidebar") {
    // Sidebar variant - render navigation items directly
    return (
      <Box sx={{ width: "100%" }}>
        <AppNavMenuItems
          user={user}
          team={team}
          activeTab={activeTab}
          onNavigate={onNavigate}
        />
        {team && (
          <>
            <Divider sx={{ my: 1 }} />
            <TeamNav
              user={user}
              team={team}
              isTeamAdmin={isTeamAdmin}
              activeTab={activeTab}
              onNavigate={onNavigate}
            />
          </>
        )}
      </Box>
    );
  }

  // Dropdown variant - render dropdown menu
  return (
    <Box>
      <Button
        id="app-nav-button"
        aria-controls={open ? "app-nav-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="outlined"
        endIcon={<ExpandMore />}
        startIcon={team ? <Groups /> : <Person />}
        sx={{
          textTransform: "none",
          justifyContent: "space-between",
          minWidth: 200,
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
          "&:hover": {
            borderColor: theme.palette.readable.link,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: "0.75rem" }}>
            {team
              ? team.name.charAt(0).toUpperCase()
              : getUserDisplayName().charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" noWrap>
            {getDropdownDisplayName()}
          </Typography>
        </Box>
      </Button>

      <Menu
        id="app-nav-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "app-nav-button",
          sx: { py: 0 },
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 280,
            maxWidth: 320,
            mt: 1,
            "& .MuiMenuItem-root": {
              px: 0,
            },
          },
        }}
      >
        {/* User/Team Info Header */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: "surfaces.surfaceRaised" }}>
          <Typography variant="body2" color="text.secondary">
            {team ? "Team" : "User"}
          </Typography>
          <Typography variant="body1" fontWeight="medium" noWrap>
            {getDropdownDisplayName()}
          </Typography>
          {user && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Navigation Items */}
        <MenuItem sx={{ p: 0 }}>
          <AppNavMenuItems
            user={user}
            team={team}
            activeTab={activeTab}
            onNavigate={handleMenuNavigate}
          />
        </MenuItem>

        {/* Team Navigation (if team context exists) */}
        {team && (
          <>
            <Divider />
            <MenuItem sx={{ p: 0 }}>
              <TeamNav
                user={user}
                team={team}
                isTeamAdmin={isTeamAdmin}
                activeTab={activeTab}
                onNavigate={handleMenuNavigate}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
}
